import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath, useFollowSwitcher } from '../lib/clientLink.js';

// Session 5: agency-only onboarding stepper for one client. Reads
// v_portal_onboarding (agency-only view). Polls every 15s while something
// is in flight.

const POLL_MS = 15000;

// Session 55: the pack fields the drafter leans on, in Leena's words
const MISSING_LABEL = {
  byline: 'author', allowlist: 'pages we may link to', money_pages: 'money pages', proof: 'proof',
  competitors: 'competitors', ga4: 'GA4', gsc: 'Search Console', approver: 'approver', description: 'what they do',
};

function Step({ n, title, state, children }) {
  const icon = state === 'done' ? '✓' : state === 'active' ? '•' : state === 'error' ? '!' : n;
  return (
    <li className={`step step-${state}`}>
      <span className="step-icon" aria-hidden="true">{icon}</span>
      <div className="step-body">
        <div className="step-title">{title}</div>
        {children && <div className="step-detail">{children}</div>}
      </div>
    </li>
  );
}

export function inviteSummary(row) {
  const st = row?.invite_status;
  if (!st) return { state: 'todo', text: 'Not queued' };
  if (st === 'queued' || st === 'running') return { state: 'active', text: 'Sending the invite…' };
  if (st === 'failed') return { state: 'error', text: 'The invite didn\'t go through. Leena has the details by email.' };
  const results = row.invite_results || [];
  const lines = results.map((r) => {
    if (r.outcome === 'skipped-agency') return `${r.email}: skipped, that's an agency account`;
    const kind = (r.mode || '').startsWith('reset') ? 'password reset email sent' : 'invite sent';
    return r.repointed_from
      ? `${r.email}: ${kind}. Their existing login now points to this client (it was ${r.repointed_from}).`
      : `${r.email}: ${kind}`;
  });
  return { state: 'done', text: lines.join('\n') || 'Invite sent' };
}

export default function AdminOnboarding() {
  const { clientId } = useParams();
  const { isAgency, setActiveId, activeId } = useAuth();
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);

  const [overview, setOverview] = useState(null);   // Session 55: pack_missing, stage, billing

  const load = useCallback(async () => {
    const [{ data, error: err }, ov] = await Promise.all([
      supabase.from('v_portal_onboarding').select('*').eq('client_id', clientId).maybeSingle(),
      supabase.from('v_portal_clients_overview').select('pack_missing, stage, plan_names, amount_cents, bill_period, onboarding_completed_at')
        .eq('client_id', clientId).maybeSingle(),
    ]);
    if (err) { setError(err.message); return null; }
    setRow(data);
    if (!ov.error) setOverview(ov.data);
    return data;
  }, [clientId]);

  useEffect(() => { if (clientId && clientId !== activeId) setActiveId(clientId); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  useFollowSwitcher(clientId, (id) => `/admin/clients/${id}/onboarding`);   // Session 17

  useEffect(() => {
    let timer;
    let cancelled = false;
    const tick = async () => {
      const r = await load();
      if (cancelled) return;
      const busy = r && (['queued', 'running'].includes(r.invite_status)
        || ['queued', 'running'].includes(r.baseline_status)
        || r.plan_status === 'generating');
      timer = setTimeout(tick, busy ? POLL_MS : POLL_MS * 4);
    };
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [load]);

  if (!isAgency) return <Navigate to="/" replace />;
  if (error) return <div className="empty"><h2>Couldn't load onboarding</h2><p>{error}</p></div>;
  if (!row) return <p className="lede">Loading…</p>;

  const invite = inviteSummary(row);
  const steps = stepStates(row);

  return (
    <>
      <h1>Onboarding: {row.name}</h1>
      <p className="lede">Each step unlocks the next. This page refreshes on its own.
        {' '}<Link to={clientPath(`/admin/clients/${clientId}/settings`, clientId)}>Open client settings</Link> for the full checklist, the analysis archive and editing.</p>
      {/* Session 55 (A6): what the pack is still missing, with the way to fill it */}
      {overview && (overview.pack_missing || []).length > 0 && (
        <div className="banner banner-info">
          Still missing in their pack: {(overview.pack_missing || []).map((k) => MISSING_LABEL[k] || k).join(', ')}.{' '}
          <Link to={clientPath(`/admin/clients/${clientId}/settings?tab=edit`, clientId)}>Edit client</Link>
          {row.client_status === 'onboarding' && ' — or wait: the client is asked for the author, links and proof on their Welcome screen.'}
        </div>
      )}
      {overview && (overview.plan_names || overview.amount_cents != null) && (
        <p className="hint">
          {overview.plan_names || 'No plan picked'}
          {overview.amount_cents != null && <> · pays ${(overview.amount_cents / 100).toFixed(overview.amount_cents % 100 ? 2 : 0)} {({ monthly: 'a month', annual: 'a year', one_off: 'one-off' })[overview.bill_period] || ''}</>}
          {overview.onboarding_completed_at && <> · confirmed their details</>}
        </p>
      )}
      <ol className="stepper">
        <Step n={1} title="Client created" state="done" />
        <Step n={2} title="Invite sent" state={invite.state}>
          <span style={{ whiteSpace: 'pre-line' }}>{invite.text}</span>
        </Step>
        <Step n={3} title="Baseline analysis" state={steps.baseline}>
          <BaselineControls row={row} onChange={load} />
        </Step>
        <Step n={4} title="Baseline strategy approved" state={steps.strategy}>
          {steps.strategy === 'done' ? 'Approved. The Strategy page now shows it.' : null}
        </Step>
        <Step n={5} title="First quarterly plan" state={steps.plan}>
          {row.plan_job_id && (
            <Link to={clientPath(`/admin/plans/${row.plan_job_id}`, row.client_id)}>Open the plan</Link>
          )}
        </Step>
        <Step n={6} title="Plan approved by client" state={steps.clientApproved} />
        <Step n={7} title="Posts scheduled" state={steps.scheduled}>
          {row.posts_scheduled ? `${row.posts_scheduled} posts have their questions scheduled.` : null}
        </Step>
      </ol>
    </>
  );
}

export function stepStates(row) {
  const b = row.baseline_status;
  const p = row.plan_status;
  return {
    baseline: !b ? 'todo'
      : ['queued', 'running'].includes(b) ? 'active'
      : b === 'failed' ? 'error'
      : 'done',
    strategy: b === 'approved' ? 'done' : b === 'agency_review' ? 'active' : 'todo',
    plan: !p ? 'todo' : ['agency_review', 'revising', 'generating'].includes(p) ? 'active' : 'done',
    clientApproved: p === 'approved' ? 'done' : p === 'sent' ? 'active' : 'todo',
    scheduled: row.posts_scheduled > 0 ? 'done' : 'todo',
  };
}

// Session 5 Part C: Run SEO analysis / Upload analysis instead.
function BaselineControls({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const st = row.baseline_status;
  const running = ['queued', 'running'].includes(st);
  const upload = row.analysis_upload;

  async function run() {
    setBusy(true); setMsg(null);
    const { error } = await supabase.rpc('portal_request_analysis', { p_client_id: row.client_id, p_notes: null });
    setBusy(false);
    if (error) setMsg({ tone: 'err', text: error.message });
    onChange();
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(pdf|md|markdown|txt)$/i.test(file.name)) {
      setMsg({ tone: 'err', text: 'Please choose a PDF or Markdown file.' });
      return;
    }
    setBusy(true); setMsg(null);
    const safe = file.name.replace(/[^A-Za-z0-9._-]+/g, '_');
    const path = `${row.client_id}/analysis/uploads/${Date.now()}-${safe}`;
    const { error: upErr } = await supabase.storage.from('post-packages')
      .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
    if (upErr) { setBusy(false); setMsg({ tone: 'err', text: `Upload failed: ${upErr.message}` }); return; }
    const { data, error } = await supabase.rpc('portal_attach_analysis', { p_client_id: row.client_id, p_storage_path: path });
    setBusy(false);
    if (error) setMsg({ tone: 'err', text: error.message });
    else setMsg({ tone: 'ok', text: running
      ? 'Uploaded. The analysis that is starting now will use it as the main source.'
      : 'Uploaded. The next analysis run will use it as the main source.' });
    onChange();
  }

  return (
    <>
      {running && <div>Analysing the site… usually 10 to 20 minutes.</div>}
      {st === 'failed' && <div className="field-hint err">The last run failed: {row.baseline_error || 'see the email for details'}.</div>}
      {st === 'agency_review' && (
        <div>Ready for your review. <Link to={clientPath(`/admin/clients/${row.client_id}/analysis`, row.client_id)}>Open the analysis</Link></div>
      )}
      {st === 'approved' && (
        <div>Approved. <Link to={clientPath(`/admin/clients/${row.client_id}/analysis`, row.client_id)}>View the analysis</Link></div>
      )}
      {upload?.path && <div className="field-hint">Uploaded analysis: {upload.path.split('/').pop()}</div>}
      {msg && <div className={`banner banner-${msg.tone}`} style={{ marginTop: 8 }}>{msg.text}</div>}
      {!['agency_review', 'approved'].includes(st) && (
        <div className="review-actions">
          <button type="button" className="btn btn-primary" disabled={busy || running} onClick={run}>
            {st === 'failed' ? 'Run SEO analysis again' : 'Run SEO analysis'}
          </button>
          <label className="btn" aria-disabled={busy}>
            Upload analysis instead
            <input type="file" accept=".pdf,.md,.markdown,.txt" hidden disabled={busy} onChange={onFile} />
          </label>
        </div>
      )}
    </>
  );
}
